import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useEffect, useState } from 'react';

const GRADIENT_COLORS = {
  light: {
    primary: 'rgba(59, 130, 246, 0.03)',
    secondary: 'rgba(99, 102, 241, 0.03)',
  },
  dark: {
    primary: 'rgba(29, 78, 216, 0.15)',
    secondary: 'rgba(124, 58, 237, 0.15)',
  }
};

export function MainBackground() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const colors = isDark ? GRADIENT_COLORS.dark : GRADIENT_COLORS.light;
  
  // Mouse position values
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Smooth spring animation for mouse movement
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  // Transform mouse position to gradient position
  const gradientX = useTransform(springX, [-1000, 1000], ['-20%', '120%']);
  const gradientY = useTransform(springY, [-1000, 1000], ['-20%', '120%']);

  // Update mouse position and handle interactions
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX - window.innerWidth / 2);
      mouseY.set(e.clientY - window.innerHeight / 2);
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [mouseX, mouseY]);

  return (
    <motion.div
      className="fixed inset-0 -z-1 overflow-hidden pointer-events-none"
      initial={false}
    >
      <motion.div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at var(--x) var(--y), ${colors.primary}, transparent ${isHovered ? '60%' : '70%'})`,
          '--x': gradientX,
          '--y': gradientY,
          opacity: isHovered ? 1 : 0.8,
          transition: 'opacity 0.3s ease-out',
        } as any}
      />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            `radial-gradient(circle at 50% 50%, ${colors.secondary} 0%, transparent ${isHovered ? '60%' : '70%'})`,
            `radial-gradient(circle at 100% 0%, ${colors.secondary} 0%, transparent ${isHovered ? '60%' : '70%'})`,
            `radial-gradient(circle at 0% 100%, ${colors.secondary} 0%, transparent ${isHovered ? '60%' : '70%'})`,
            `radial-gradient(circle at 50% 50%, ${colors.secondary} 0%, transparent ${isHovered ? '60%' : '70%'})`
          ],
        }}
        style={{
          opacity: isHovered ? 1 : 0.8,
          transition: 'opacity 0.3s ease-out',
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
      />
    </motion.div>
  );
}